import { useEffect, useState } from 'react'
import { Box, Typography, Button } from '@mui/material'
import { IconContainer } from 'components/atoms/Icons'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import InCardStack, { ColStack } from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import { JOURNEY } from 'routes/constants'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import colors from 'styles/colors'
import { getGoalName, getCohortGoalFacilitation } from 'utils/getSupportingInfo'
import { FacilitatedSessionsDisplay } from 'components/molecules/adminDashboard/cohorts/FacilitatedSessions'
import moment from 'moment'
import { formatDate } from 'utils/formatDateTime'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'

const CohortSessionBox = ({ goal }) => {
  const { t } = useTranslation()
  const [nextFacilitationDate, setNextFacilitationDate] = useState(null)
  const [isCompleted, setIsCompleted] = useState(false)
  const cohortSession = goal?.cohortSession
  const sessionsStarted = moment().isAfter(
    moment(cohortSession?.startDate).startOf('day')
  )

  useEffect(() => {
    const fetchGoalSessions = async () => {
      const sessionData = await getCohortGoalFacilitation(
        cohortSession.id,
        goal?.l1Steps
      )

      // find the facilitationDate from the sessionData
      const nextSession = sessionData?.find(session =>
        moment(session.cohortSession?.facilitationDate)
          .startOf('day')
          .isAfter(moment().startOf('day'))
      )
      if (nextSession?.cohortSession?.facilitationDate) {
        setNextFacilitationDate(nextSession?.cohortSession?.facilitationDate)
      } else {
        setIsCompleted(true)
      }
    }
    if (goal && sessionsStarted) {
      fetchGoalSessions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal])

  return (
    <FacilitatedSessionsDisplay
      cohortSessionId={cohortSession?.id}
      goalId={goal?.id}>
      <Box
        sx={{
          paddingX: 6,
          paddingY: 8,
          width: 216,
          borderRadius: 3,
          backgroundColor: colors.White
        }}>
        <InCardStack spacing={4}>
          <Typography
            variant='body1Small'
            color='textSecondary'
            textAlign={'center'}>
            {!sessionsStarted
              ? t('profile.sessionsStart')
              : isCompleted
                ? t('profile.cohortCompleted')
                : t('profile.nextFacilitatedSession')}
          </Typography>
          <Box
            sx={{
              paddingX: 4,
              paddingY: 3,
              borderRadius: 2,
              backgroundColor: isCompleted
                ? colors.Green100
                : colors.MutedTeal100
            }}>
            {isCompleted ? (
              <RowStack spacing={2}>
                <IconContainer
                  icon={<CheckCircleOutlineRoundedIcon />}
                  color={colors.Green400}
                />
                <Typography variant='h4' color={colors.Green400}>
                  {t('common.completed')}
                </Typography>
              </RowStack>
            ) : (
              <Typography variant='h4'>
                {formatDate(
                  !sessionsStarted
                    ? cohortSession?.startDate
                    : nextFacilitationDate
                )}
              </Typography>
            )}
          </Box>
          <Typography variant='body1Small' color='textSecondary'>
            {t('profile.seeSchedule')}
          </Typography>
        </InCardStack>
      </Box>
    </FacilitatedSessionsDisplay>
  )
}

const CurrentGoalSection = ({ goal, isCohortUser }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goalName = getGoalName(goal)
  return (
    <BoxWithBackground padding={8} borderRadius={3} width='100%'>
      <RowStack spacing={8} justifyContent='space-between' alignItems='center'>
        <ColStack spacing={8} minWidth={240} flex={1}>
          <ColStack spacing={2}>
            <Typography variant='eyebrow' color='textSecondary'>
              {t('pathsAndModels.currentPath')}
            </Typography>

            <Typography variant='h4'>
              {goalName}
            </Typography>
          </ColStack>
          <Button size='small' onClick={() => navigate(JOURNEY)}>
            {t('pathsAndModels.jumpBackIn')}
          </Button>
        </ColStack>

        {isCohortUser && goal?.cohortSession ? (
          <CohortSessionBox goal={goal} />
        ) : null}
      </RowStack>
    </BoxWithBackground>
  )
}

export default CurrentGoalSection
